import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check, Loader2, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { DATA_LANCAMENTO, NOME_TURMA, PLANOS, TRIAL_DIAS, VAGAS_FUNDADORAS } from "@/data/clube";

export interface LeadVip {
  nome?: string;
  telefone?: string;
  email?: string;
}

/** Envia a pessoa para a lista VIP das fundadoras (função já publicada, form_type "lista_vip"). */
export const entrarListaVip = async (lead: LeadVip, origem: string) => {
  const { error } = await supabase.functions.invoke("submit-questionnaire", {
    body: {
      form_type: "lista_vip",
      nome: (lead.nome ?? "").trim(),
      telefone: (lead.telefone ?? "").trim(),
      email: (lead.email ?? "").trim().toLowerCase(),
      origem: typeof window !== "undefined" ? `${origem} · ${window.location.href}` : origem,
    },
  });
  if (error) throw error;
  trackEvent("vip_list_join", { origem });
};

interface Props {
  /** Identifica de onde veio (vai no e-mail para a Clara e no evento do GA). */
  origem: string;
  /** Dados já conhecidos (ex.: captura do quiz) para preencher. */
  lead?: LeadVip;
  /** claro: fundo branco; escuro: dentro de card verde. */
  variante?: "claro" | "escuro";
  /** Texto do botão. */
  cta?: string;
  /** Mostra o link para o diagnóstico depois de entrar. */
  sugerirDiagnostico?: boolean;
  onDone?: () => void;
}

const ListaVipForm = ({ origem, lead, variante = "claro", cta = "Quero minha vaga", sugerirDiagnostico = true, onDone }: Props) => {
  const [nome, setNome] = useState(lead?.nome ?? "");
  const [telefone, setTelefone] = useState(lead?.telefone ?? "");
  const [email, setEmail] = useState(lead?.email ?? "");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();
  const escuro = variante === "escuro";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nome.trim().length < 2 || telefone.trim().length < 6) {
      toast({ title: "Preencha nome e WhatsApp", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await entrarListaVip({ nome, telefone, email }, origem);
      setDone(true);
      onDone?.();
    } catch (err) {
      console.error(err);
      toast({ title: "Ops, algo deu errado", description: "Tente novamente em instantes.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const inputCls = escuro
    ? "h-13 w-full rounded-2xl border border-white/30 bg-white/15 px-4 py-3.5 text-[16px] text-white placeholder:text-white/60 outline-none focus:border-white focus:bg-white/20 font-sans"
    : "h-13 w-full rounded-2xl border px-4 py-3.5 text-[16px] outline-none transition-shadow font-sans placeholder:text-[#A1A1AA] focus:shadow-[0_0_0_2px_#0B0B0F]";
  const labelCls = escuro ? "mb-1.5 block text-[12px] font-semibold uppercase tracking-wider text-white/80 font-sans" : "mb-1.5 block text-[13px] font-medium font-sans";
  const inputStyle = escuro ? undefined : { borderColor: "hsl(32 20% 88%)", color: "hsl(30 10% 18%)", background: "#fff" };

  if (done) {
    return (
      <div className={`space-y-3 text-center ${escuro ? "text-white" : ""}`}>
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{ background: escuro ? "rgba(255,255,255,0.2)" : "hsl(150 22% 35% / 0.12)", color: escuro ? "#fff" : "hsl(150 22% 35%)" }}>
          <Check className="h-6 w-6" strokeWidth={3} />
        </span>
        <p className="text-xl font-extrabold tracking-tight font-sans">Você está na lista!</p>
        <p className={`text-sm leading-relaxed font-sans ${escuro ? "text-white/85" : ""}`} style={escuro ? undefined : { color: "hsl(30 10% 33%)" }}>
          No dia {DATA_LANCAMENTO} as {VAGAS_FUNDADORAS} vagas da {NOME_TURMA} abrem primeiro para quem está aqui, por A$ {PLANOS.fundadora.preco} por mês para sempre. Te aviso no WhatsApp.
        </p>
        {sugerirDiagnostico && (
          <Link to="/diagnostico" className={`inline-block text-sm font-semibold underline underline-offset-4 font-sans ${escuro ? "text-white" : ""}`} style={escuro ? undefined : { color: "hsl(150 22% 35%)" }}>
            Enquanto isso, descubra o seu perfil no diagnóstico de 3 minutos
          </Link>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label htmlFor={`vip-nome-${origem}`} className={labelCls} style={escuro ? undefined : { color: "hsl(30 10% 33%)" }}>
          Primeiro nome
        </label>
        <input id={`vip-nome-${origem}`} type="text" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Seu primeiro nome" autoComplete="given-name" maxLength={100} required className={inputCls} style={inputStyle} />
      </div>
      <div>
        <label htmlFor={`vip-tel-${origem}`} className={labelCls} style={escuro ? undefined : { color: "hsl(30 10% 33%)" }}>
          WhatsApp
        </label>
        <input id={`vip-tel-${origem}`} type="tel" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="04XX XXX XXX" autoComplete="tel" maxLength={30} required className={inputCls} style={inputStyle} />
      </div>
      <div>
        <label htmlFor={`vip-email-${origem}`} className={labelCls} style={escuro ? undefined : { color: "hsl(30 10% 33%)" }}>
          E-mail <span className="font-normal normal-case tracking-normal opacity-70">(opcional)</span>
        </label>
        <input id={`vip-email-${origem}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Para receber o acesso no lançamento" autoComplete="email" maxLength={120} className={inputCls} style={inputStyle} />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="flex h-14 w-full items-center justify-center rounded-2xl text-[16px] font-extrabold uppercase tracking-wide transition-transform active:scale-[.98] disabled:opacity-60 font-sans"
        style={
          escuro
            ? { background: "#fff", color: "hsl(150 25% 28%)" }
            : { background: "linear-gradient(100deg, hsl(150 25% 32%), hsl(150 22% 35%), hsl(150 22% 40%), hsl(155 30% 50%))", color: "#fff", boxShadow: "0 8px 28px hsl(150 22% 35% / 0.25)" }
        }
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Enviando…
          </>
        ) : (
          cta
        )}
      </button>
      <p className={`flex items-center justify-center gap-1.5 text-center text-[11px] font-sans ${escuro ? "text-white/75" : ""}`} style={escuro ? undefined : { color: "hsl(30 10% 50%)" }}>
        <Lock className="h-3 w-3" /> Sem compromisso. Entrar na lista não é compra. {TRIAL_DIAS} dias grátis no lançamento.
      </p>
    </form>
  );
};

export default ListaVipForm;
